import { NotificationSeverity } from '@/components/notifications/Notification';
import { useGetDriversQuery } from '@/pages/Business/pages/Drivers/services';
import { useAssignShippingMutation } from '../services/shippings.service';
import { useResolveRouteOperatorMutation } from '../services/routing.service';
import type { RouteOperatorCandidate } from '../models/routing.types';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import LoaderBackdrop from '@/components/loaders/LoaderBackdrop';
import { GlobalButton, SearchInput } from '@/components';
import DriverListWithPhoto from './DriverListWithPhoto';
import { getErrorMessage } from '@/utils/errorUtils';
import useDebounce from '@/hooks/useDebounce';
import { useState, useEffect } from 'react';
import { useNotification } from '@/hooks';
import { MenuItem, Modal, TextField } from '@mui/material';
import type {
  DriversSearchParams,
  DriversSearchResult,
} from '@/pages/Business/pages/Monitoring/context/monitoringIntegration.context';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;
const DRIVERS_PAGE_SIZE = 1000;

const useDefaultDriversSearch = ({
  search,
  enabled,
  logisticOperatorId,
}: DriversSearchParams): DriversSearchResult => {
  const debouncedSearch = useDebounce(search, DEBOUNCE_MS);
  const hasSearched = debouncedSearch.trim().length >= MIN_SEARCH_LENGTH;

  const {
    data: driversData,
    isLoading,
    isError,
  } = useGetDriversQuery(
    {
      paginated: { page: 1, pageSize: DRIVERS_PAGE_SIZE },
      filters: {
        assignableToOperatorId: logisticOperatorId?.trim() || '',
        ...(hasSearched ? { search: debouncedSearch.trim() } : {}),
      },
    },
    { skip: !enabled || !logisticOperatorId?.trim() || !hasSearched }
  );

  return { drivers: driversData?.items ?? [], isLoading, isError };
};

export interface AssignShippingModalProps {
  open: boolean;
  onClose: () => void;
  shippingNumber: string;
  logisticOperatorId: string;
  useDriversSearch?: (params: DriversSearchParams) => DriversSearchResult;
}

const AssignShippingModal = ({
  open,
  onClose,
  shippingNumber,
  logisticOperatorId,
  useDriversSearch = useDefaultDriversSearch,
}: AssignShippingModalProps) => {

  const [assignShipping, { isLoading: isSubmitting }] = useAssignShippingMutation();
  const [resolveRouteOperator] = useResolveRouteOperatorMutation();
  const { showNotification } = useNotification();
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [operatorCandidates, setOperatorCandidates] = useState<RouteOperatorCandidate[]>([]);
  const [routeOperatorId, setRouteOperatorId] = useState(logisticOperatorId);
  const canChooseOperator = operatorCandidates.length > 1;
  const effectiveOperatorId = canChooseOperator ? routeOperatorId : logisticOperatorId;
  const singleOperatorName =
    operatorCandidates.length === 1 ? operatorCandidates[0].logisticOperatorName : null;
  const [searchName, setSearchName] = useState('');
  const debouncedSearch = useDebounce(searchName, DEBOUNCE_MS);
  const hasSearched = debouncedSearch.trim().length >= MIN_SEARCH_LENGTH;

  const {
    drivers,
    isLoading: isLoadingDrivers,
    isError: isDriversError,
  } = useDriversSearch({
    search: searchName,
    enabled: open,
    logisticOperatorId: effectiveOperatorId,
  });

  useEffect(() => {
    if (!open) {
      setSearchName('');
      setSelectedDriverId(null);
      setOperatorCandidates([]);
      setRouteOperatorId(logisticOperatorId);
    }
  }, [open, logisticOperatorId]);

  useEffect(() => {
    const number = Number(shippingNumber);
    if (!open || !Number.isFinite(number) || number <= 0) return;

    let cancelled = false;
    resolveRouteOperator({ shippingNumbers: [number] })
      .unwrap()
      .then((result) => {
        if (cancelled) return;
        const candidates = result.candidates ?? [];
        setOperatorCandidates(candidates);
        const hasOrderOperator = candidates.some(
          (candidate) => candidate.logisticOperatorId === logisticOperatorId
        );
        setRouteOperatorId(
          hasOrderOperator ? logisticOperatorId : candidates[0]?.logisticOperatorId ?? logisticOperatorId
        );
      })
      .catch(() => {
        if (!cancelled) setOperatorCandidates([]);
      });

    return () => {
      cancelled = true;
    };
  }, [open, shippingNumber, logisticOperatorId, resolveRouteOperator]);

  const handleRouteOperatorChange = (value: string) => {
    setRouteOperatorId(value);
    setSelectedDriverId(null);
  };

  const handleAssign = async () => {
    if (!selectedDriverId) return;
    try {
      await assignShipping({
        number: shippingNumber,
        driverId: selectedDriverId,
        ...(canChooseOperator ? { routeLogisticOperatorId: routeOperatorId } : {}),
      }).unwrap();
      showNotification('Conductor asignado correctamente', NotificationSeverity.success);
      onClose();
    } catch (error) {
      showNotification(getErrorMessage(error, 'Error al asignar el conductor'), NotificationSeverity.error);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl'>
        {isSubmitting && <LoaderBackdrop />}
        <div className='flex flex-col p-6 gap-4 bg-white outline-none rounded-lg shadow-xl max-h-[90vh]'>
          <h2 className='text-lg font-semibold text-gray-800'>Asignar conductor</h2>

          {canChooseOperator ? (
            <TextField
              select
              size='small'
              label='Asignar a nombre de'
              value={routeOperatorId}
              onChange={(event) => handleRouteOperatorChange(event.target.value)}
              disabled={isSubmitting}
            >
              {operatorCandidates.map((candidate) => (
                <MenuItem key={candidate.logisticOperatorId} value={candidate.logisticOperatorId}>
                  {candidate.logisticOperatorName ?? candidate.logisticOperatorId}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            singleOperatorName && (
              <p className='-mt-2 text-sm text-gray-600'>
                Ruta a nombre de <span className='font-semibold'>{singleOperatorName}</span>
              </p>
            )
          )}

          <SearchInput
            name='driverSearch'
            placeholder={`Buscar por nombre (mín. ${MIN_SEARCH_LENGTH} caracteres)`}
            value={searchName}
            onChange={setSearchName}
          />

          {hasSearched && (
            <div className='flex flex-col min-h-[240px]'>
              <DriverListWithPhoto
                loading={isLoadingDrivers}
                error={isDriversError}
                drivers={drivers}
                selectedId={selectedDriverId}
                onSelect={setSelectedDriverId}
              />
            </div>
          )}

          <div className='flex gap-3 justify-end pt-2'>
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </GlobalButton>
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={handleAssign}
              disabled={!selectedDriverId || isSubmitting}
            >
              {isSubmitting ? 'Asignando…' : 'Asignar'}
            </GlobalButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AssignShippingModal;
